import { useEffect } from "react";
import { apiRequest } from "../services/api";

const SITE_URL = "https://chiaca.co";

const absoluteUrl = (url) => {
  if (!url) return `${SITE_URL}/logo-512x512.png`;
  if (String(url).startsWith("http")) return url;
  return `${SITE_URL}${String(url).startsWith("/") ? url : `/${url}`}`;
};

const buildCanonicalFromCurrentUrl = () => {
  const url = new URL(window.location.href);

  // remove language only; keep meaningful SEO filters like category
  url.searchParams.delete("lang");

  return `${SITE_URL}${url.pathname}${url.search}`;
};

export const useSeo = (pageKey, entityId = null, schema = null) => {
  useEffect(() => {
const fallbackCanonical = buildCanonicalFromCurrentUrl();

apiRequest(
  entityId ? `/seo/${pageKey}?entity=${entityId}` : `/seo/${pageKey}`
)
  .then((seo) => {
    // ✅ Always prevent stale canonical from previous SPA page
    setLink("canonical", seo?.canonical || fallbackCanonical);
    setMeta("og:url", seo?.canonical || fallbackCanonical, "property");

    if (!seo) {
      setMeta("robots", "index,follow");
      return;
    }

        // ===== TITLE =====
        if (seo.title) {
          document.title = seo.title;
          setMeta("og:title", seo.title, "property");
        }

        if (seo.json_ld) {
          try {
            injectSchema(JSON.parse(seo.json_ld));
          } catch {
            // ignore invalid JSON-LD safely
          }
        }

        // ===== DESCRIPTION =====
        if (seo.meta_description) {
          setMeta("description", seo.meta_description);
          setMeta("og:description", seo.meta_description, "property");
        }

        // ===== KEYWORDS =====
        setMeta("keywords", seo.meta_keywords);

        // ===== CANONICAL =====
        setLink("canonical", seo.canonical || fallbackCanonical);
setMeta("og:url", seo.canonical || fallbackCanonical, "property");

        // ===== ROBOTS =====
        setMeta(
          "robots",
          seo.is_indexable ? "index,follow" : "noindex,nofollow"
        );

        // ===== OPEN GRAPH =====
        setMeta("og:type", seo.og_type || "website", "property");
        setMeta(
          "og:image",
          absoluteUrl(seo.og_image_url || "/logo-512x512.png"),
          "property"
        );

        // ===== TWITTER =====
        setMeta("twitter:card", "summary_large_image");
        setMeta("twitter:title", seo.title);
        setMeta("twitter:description", seo.meta_description);
        setMeta(
          "twitter:image",
          absoluteUrl(seo.og_image_url || "/logo-512x512.png")
        );

        // ===== HREFLANG =====
        if (seo.hreflang) {
          Object.entries(seo.hreflang).forEach(([lang, url]) => {
            setAltLink("alternate", url, lang);
          });
        }

        // ===== STRUCTURED DATA FROM PAGE =====
        if (schema) {
          injectSchema(schema);
        }
      })
      .catch(() => {});
  }, [pageKey, entityId, schema]);
};

/* ================= HELPERS ================= */

const setMeta = (name, content, attr = "name") => {
  if (!content) return;

  let el = document.querySelector(`meta[${attr}="${name}"]`);

  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }

  el.setAttribute("content", content);
};

const setLink = (rel, href) => {
  let el = document.querySelector(`link[rel="${rel}"]`);

  if (!el) {
    el = document.createElement("link");
    el.rel = rel;
    document.head.appendChild(el);
  }

  el.href = href;
};

const setAltLink = (rel, href, lang) => {
  let el = document.querySelector(
    `link[rel="${rel}"][hreflang="${lang}"]`
  );

  if (!el) {
    el = document.createElement("link");
    el.rel = rel;
    el.hreflang = lang;
    document.head.appendChild(el);
  }

  el.href = href;
};

const injectSchema = (schema) => {
  if (!schema) return;

  const old = document.getElementById("seo-schema");
  if (old) old.remove();

  const el = document.createElement("script");
  el.type = "application/ld+json";
  el.id = "seo-schema";
  el.textContent = JSON.stringify(schema);
  document.head.appendChild(el);
};
